<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { DateTime } from 'luxon'

import { useCalculationStore } from '@/stores/calculation'
import type { CalculationComputeResource } from '@/stores/type/calculation.type'
import ComputeResourceLogsDialog from '@/components/calculation-result/ComputeResourceLogsDialog.vue'

const calculationStore = useCalculationStore()
const {
  currentItem,
  computeResources,
  historicalComputeResources,
  computeResourcesLoading,
  historicalComputeResourcesLoading
} = storeToRefs(calculationStore)

const logsDialog = ref(false)
const selectedResource = ref<CalculationComputeResource | null>(null)

const hasComputeResources = computed(() => computeResources.value.length > 0)
const hasHistoricalComputeResources = computed(() => historicalComputeResources.value.length > 0)

const resourceLabel = computed(() => 'Virtual machine')

const resourceLabelPlural = computed(() => 'Virtual machines')

const platformStatusHeader = computed(() => 'Power state')

const runStatusHeader = computed(() => 'Run status')

const emptyStateText = computed(
  () =>
    `No Azure ${resourceLabelPlural.value.toLowerCase()} are currently present for this calculation.`
)
const isTerminalCalculation = computed(() => {
  return ['DONE', 'ERROR', 'UPLOAD_FAILED', 'TIMEOUT', 'CANCELLED'].includes(
    String(currentItem.value?.status ?? '')
  )
})

const refreshComputeResources = async () => {
  if (!currentItem.value?.id) return
  await calculationStore.getComputeResources(currentItem.value.id)
}

const refreshHistoricalComputeResources = async () => {
  if (!currentItem.value?.id) return
  await calculationStore.getHistoricalComputeResources(currentItem.value.id)
}

watch(
  () => [
    currentItem.value?.id,
    currentItem.value?.status,
    currentItem.value?.compute_resource_status
  ],
  async ([calculationId]) => {
    if (!calculationId) return
    await Promise.all([
      calculationStore.getComputeResources(Number(calculationId)),
      calculationStore.getHistoricalComputeResources(Number(calculationId))
    ])
  },
  { immediate: true }
)

const openComputeResourceLogs = (resource: CalculationComputeResource) => {
  selectedResource.value = resource
  logsDialog.value = true
}

const resourceIcon = (resource: CalculationComputeResource) => {
  const resourceType = resource.resource_type ?? currentItem.value?.hardware_profile_type
  return resourceType === 'VIRTUAL_MACHINE' ? 'mdi-server' : 'mdi-console-line'
}

const statusColor = (status?: string | null) => {
  if (!status) return 'default'
  const normalized = status.toLowerCase()
  if (normalized.includes('succeeded')) return 'success'
  if (normalized.includes('failed') || normalized.includes('stopped')) return 'error'
  if (normalized.includes('repairing') || normalized.includes('unhealthy')) return 'warning'
  if (
    normalized.includes('running') ||
    normalized.includes('uploading') ||
    normalized.includes('creating') ||
    normalized.includes('pending')
  ) {
    return 'primary'
  }
  return 'default'
}

const displayValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') return '-'
  const label = String(value)
  return `${label.charAt(0).toUpperCase()}${label.slice(1)}`
}

const formatCores = (value?: number | null) => {
  if (value === null || value === undefined) return '-'
  return Number.isInteger(value) ? value.toString() : value.toFixed(2)
}

const formatMemory = (value?: number | null) => {
  if (value === null || value === undefined) return '-'
  return `${formatCompactNumber(value)}GB`
}

const diskTypeLabels: Record<string, string> = {
  Standard_LRS: 'Standard HDD',
  StandardSSD_LRS: 'Standard SSD',
  Premium_LRS: 'Premium SSD'
}

const formatOsDisk = (resource: CalculationComputeResource) => {
  if (resource.os_disk_size_gb === null || resource.os_disk_size_gb === undefined) return '-'
  return `${formatCompactNumber(resource.os_disk_size_gb)}GB`
}

const osDiskTooltip = (resource: CalculationComputeResource) => {
  if (resource.os_disk_size_gb === null || resource.os_disk_size_gb === undefined) return ''
  const type = resource.os_disk_storage_account_type
    ? (diskTypeLabels[resource.os_disk_storage_account_type] ??
      resource.os_disk_storage_account_type)
    : ''
  return `${formatCompactNumber(resource.os_disk_size_gb)} GB${type ? ` ${type}` : ''}`
}

const formatPercent = (value?: number | null) => {
  if (value === null || value === undefined) return '-'
  return `${value.toFixed(1)}%`
}

const formatMoney = (value?: number | null, currency = 'USD') => {
  if (value === null || value === undefined) return '-'
  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: value < 1 ? 4 : 2
  })
  return formatter.format(value)
}

const formatHourlyPrice = (resource: CalculationComputeResource) => {
  if (resource.price_per_hour === null || resource.price_per_hour === undefined) return '-'
  return formatMoney(resource.price_per_hour, resource.price_currency ?? 'USD')
}

const formatEstimatedCost = (resource: CalculationComputeResource) => {
  return formatMoney(resource.estimated_cost, resource.price_currency ?? 'USD')
}

const priceTooltip = (resource: CalculationComputeResource) => {
  if (resource.price_per_hour === null || resource.price_per_hour === undefined) {
    return 'No Azure retail price was found for this resource.'
  }

  const parts = [
    priceLabelWithoutDisk(resource.price_product_name, resource.os_disk_price_product_name),
    priceLabelWithoutDisk(resource.price_meter_name, resource.os_disk_price_meter_name),
    resource.compute_price_per_hour !== null && resource.compute_price_per_hour !== undefined
      ? `Compute ${formatMoney(resource.compute_price_per_hour, resource.price_currency ?? 'USD')}/h`
      : null,
    resource.os_disk_price_per_hour !== null && resource.os_disk_price_per_hour !== undefined
      ? `OS disk ${formatMoney(resource.os_disk_price_per_hour, resource.price_currency ?? 'USD')}/h`
      : null
  ].filter(Boolean)
  return parts.join(' · ')
}

const priceLabelWithoutDisk = (value?: string | null, diskValue?: string | null) => {
  if (!value) return null
  if (!diskValue) return value
  const parts = value.split(' + ').filter((part) => part !== diskValue)
  return parts.join(' + ') || null
}

const isLowUsage = (value?: number | null) => {
  return value !== null && value !== undefined && value < 85
}

const numberOrNull = (value?: number | null) => {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

const cpuUsagePercent = (resource: CalculationComputeResource) => {
  if (isTerminalCalculation.value) return null

  const reportedPercent = numberOrNull(resource.cpu_usage_percent)
  if (reportedPercent !== null) return reportedPercent

  const usageCores = numberOrNull(resource.cpu_usage_cores)
  if (usageCores !== null && resource.cpu_cores) {
    return (usageCores / resource.cpu_cores) * 100
  }
  return null
}

const memoryUsagePercent = (resource: CalculationComputeResource) => {
  if (isTerminalCalculation.value) return null

  const reportedPercent = numberOrNull(resource.memory_usage_percent)
  if (reportedPercent !== null) return reportedPercent

  const usageGb = numberOrNull(resource.memory_usage_gb)
  if (usageGb !== null && resource.memory_gb) {
    return (usageGb / resource.memory_gb) * 100
  }
  return null
}

const osDiskUsagePercent = (resource: CalculationComputeResource) => {
  if (isTerminalCalculation.value) return null

  const reportedPercent = numberOrNull(resource.os_disk_usage_percent)
  if (reportedPercent !== null) return reportedPercent

  const usageGb = numberOrNull(resource.os_disk_usage_gb)
  if (usageGb !== null && resource.os_disk_size_gb) {
    return (usageGb / resource.os_disk_size_gb) * 100
  }
  return null
}

const formatDate = (value?: string | null) => {
  if (!value) return '-'
  return DateTime.fromISO(value).toFormat('dd LLL HH:mm')
}

const formatCompactNumber = (value: number) => {
  return Number.isInteger(value) ? value.toString() : value.toFixed(1)
}
</script>

<template>
  <div class="compute-resource-tables">
    <v-card class="compute-resource-card" border="opacity-25 sm" elevation="0" flat>
      <v-toolbar density="compact" color="transparent">
        <v-toolbar-title class="text-subtitle-1 font-weight-medium">
          {{ resourceLabelPlural }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn
          icon="mdi-refresh"
          variant="text"
          :loading="computeResourcesLoading"
          @click="refreshComputeResources"
        />
      </v-toolbar>

      <v-progress-linear v-if="computeResourcesLoading" indeterminate color="primary" />

      <div class="resource-table-wrapper">
        <v-table density="compact" class="resource-instance-table">
          <colgroup>
            <col class="name-column" />
            <col class="status-column" />
            <col class="status-column" />
            <col class="resource-column" />
            <col class="resource-column" />
            <col class="resource-column" />
            <col class="price-column" />
            <col class="price-column" />
            <col class="usage-column" />
            <col class="usage-column" />
            <col class="usage-column" />
            <col class="actions-column" />
            <col class="date-column" />
            <col class="date-column" />
            <col class="date-column" />
          </colgroup>
          <thead>
            <tr>
              <th class="text-left">Name</th>
              <th class="text-left">{{ platformStatusHeader }}</th>
              <th class="text-left">{{ runStatusHeader }}</th>
              <th class="text-left">CPU</th>
              <th class="text-left">RAM</th>
              <th class="text-left">OS disk</th>
              <th class="text-left">Price/h</th>
              <th class="text-left">Est. cost</th>
              <th class="text-left">CPU use</th>
              <th class="text-left">RAM use</th>
              <th class="text-left">Disk use</th>
              <th class="text-left">Actions</th>
              <th class="text-left">Created</th>
              <th class="text-left">Started</th>
              <th class="text-left">Ended</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="resource in computeResources"
              :key="resource.name"
              class="resource-row"
              @click="openComputeResourceLogs(resource)"
            >
              <td class="font-weight-medium" :title="resource.name">
                <span class="resource-name-cell">
                  <v-icon :icon="resourceIcon(resource)" size="small" />
                  <span class="text-truncate">{{ resource.name }}</span>
                </span>
              </td>
              <td>
                <v-chip
                  size="small"
                  :color="statusColor(resource.group_status || resource.status)"
                  variant="tonal"
                >
                  {{ displayValue(resource.group_status || resource.status) }}
                </v-chip>
              </td>
              <td>
                <v-chip
                  size="small"
                  :color="statusColor(resource.container_status)"
                  variant="tonal"
                >
                  {{ displayValue(resource.container_status) }}
                </v-chip>
              </td>
              <td class="text-left">{{ formatCores(resource.cpu_cores) }}</td>
              <td class="text-left">{{ formatMemory(resource.memory_gb) }}</td>
              <td class="text-left">
                <v-tooltip
                  :text="osDiskTooltip(resource)"
                  location="top"
                  :disabled="!osDiskTooltip(resource)"
                >
                  <template #activator="{ props }">
                    <span v-bind="props">{{ formatOsDisk(resource) }}</span>
                  </template>
                </v-tooltip>
              </td>
              <td class="text-left">
                <v-tooltip :text="priceTooltip(resource)" location="top">
                  <template #activator="{ props }">
                    <span v-bind="props" class="price-cell">
                      <span>{{ formatHourlyPrice(resource) }}</span>
                      <v-chip
                        v-if="resource.spot_instance"
                        size="x-small"
                        color="warning"
                        variant="tonal"
                      >
                        Spot
                      </v-chip>
                    </span>
                  </template>
                </v-tooltip>
              </td>
              <td class="text-left">{{ formatEstimatedCost(resource) }}</td>
              <td class="text-left">
                <span class="usage-cell">
                  {{ formatPercent(cpuUsagePercent(resource)) }}
                  <v-tooltip
                    v-if="isLowUsage(cpuUsagePercent(resource))"
                    text="CPU utilization is low. A too costly workload profile might have been chosen."
                    location="top"
                  >
                    <template #activator="{ props }">
                      <v-icon v-bind="props" color="error" icon="mdi-alert-circle" size="small" />
                    </template>
                  </v-tooltip>
                </span>
              </td>
              <td class="text-left">
                <span class="usage-cell">
                  {{ formatPercent(memoryUsagePercent(resource)) }}
                  <v-tooltip
                    v-if="isLowUsage(memoryUsagePercent(resource))"
                    text="RAM utilization is low. A too costly workload profile might have been chosen."
                    location="top"
                  >
                    <template #activator="{ props }">
                      <v-icon v-bind="props" color="error" icon="mdi-alert-circle" size="small" />
                    </template>
                  </v-tooltip>
                </span>
              </td>
              <td class="text-left">
                <span class="usage-cell">
                  {{ formatPercent(osDiskUsagePercent(resource)) }}
                </span>
              </td>
              <td class="text-left resource-actions">
                <v-tooltip text="Open logs" location="top">
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="mdi-text-box-search-outline"
                      variant="text"
                      density="comfortable"
                      @click.stop="openComputeResourceLogs(resource)"
                    />
                  </template>
                </v-tooltip>
              </td>
              <td>{{ formatDate(resource.created) }}</td>
              <td>{{ formatDate(resource.started) }}</td>
              <td>{{ formatDate(resource.ended) }}</td>
            </tr>
            <tr v-if="!hasComputeResources && !computeResourcesLoading">
              <td colspan="15" class="text-left text-medium-emphasis py-8">
                {{ emptyStateText }}
              </td>
            </tr>
          </tbody>
        </v-table>
      </div>
    </v-card>

    <v-card
      v-if="hasHistoricalComputeResources"
      class="compute-resource-card historical-resource-card"
      border="opacity-25 sm"
      elevation="0"
      flat
    >
      <v-toolbar density="compact" color="transparent">
        <v-toolbar-title class="text-subtitle-1 font-weight-medium">
          Historical {{ resourceLabelPlural.toLowerCase() }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn
          icon="mdi-refresh"
          variant="text"
          :loading="historicalComputeResourcesLoading"
          @click="refreshHistoricalComputeResources"
        />
      </v-toolbar>
      <v-progress-linear v-if="historicalComputeResourcesLoading" indeterminate color="primary" />
      <p class="historical-note text-body-2 text-medium-emphasis mb-0">
        These Azure resources no longer exist. They are shown here so stored logs and final cost
        estimates remain easy to access.
      </p>

      <div class="resource-table-wrapper">
        <v-table density="compact" class="resource-instance-table">
          <colgroup>
            <col class="name-column" />
            <col class="resource-column" />
            <col class="resource-column" />
            <col class="resource-column" />
            <col class="price-column" />
            <col class="price-column" />
            <col class="usage-column" />
            <col class="usage-column" />
            <col class="usage-column" />
            <col class="actions-column" />
            <col class="date-column" />
            <col class="date-column" />
            <col class="date-column" />
          </colgroup>
          <thead>
            <tr>
              <th class="text-left">Name</th>
              <th class="text-left">CPU</th>
              <th class="text-left">RAM</th>
              <th class="text-left">OS disk</th>
              <th class="text-left">Price/h</th>
              <th class="text-left">Est. cost</th>
              <th class="text-left">CPU use</th>
              <th class="text-left">RAM use</th>
              <th class="text-left">Disk use</th>
              <th class="text-left">Actions</th>
              <th class="text-left">Created</th>
              <th class="text-left">Started</th>
              <th class="text-left">Ended</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="resource in historicalComputeResources"
              :key="resource.name"
              class="resource-row"
              @click="openComputeResourceLogs(resource)"
            >
              <td class="font-weight-medium" :title="resource.name">
                <span class="resource-name-cell">
                  <v-icon :icon="resourceIcon(resource)" size="small" />
                  <span class="text-truncate">{{ resource.name }}</span>
                </span>
              </td>
              <td class="text-left">{{ formatCores(resource.cpu_cores) }}</td>
              <td class="text-left">{{ formatMemory(resource.memory_gb) }}</td>
              <td class="text-left">
                <v-tooltip
                  :text="osDiskTooltip(resource)"
                  location="top"
                  :disabled="!osDiskTooltip(resource)"
                >
                  <template #activator="{ props }">
                    <span v-bind="props">{{ formatOsDisk(resource) }}</span>
                  </template>
                </v-tooltip>
              </td>
              <td class="text-left">
                <v-tooltip :text="priceTooltip(resource)" location="top">
                  <template #activator="{ props }">
                    <span v-bind="props" class="price-cell">
                      <span>{{ formatHourlyPrice(resource) }}</span>
                      <v-chip
                        v-if="resource.spot_instance"
                        size="x-small"
                        color="warning"
                        variant="tonal"
                      >
                        Spot
                      </v-chip>
                    </span>
                  </template>
                </v-tooltip>
              </td>
              <td class="text-left">{{ formatEstimatedCost(resource) }}</td>
              <td class="text-left">{{ formatPercent(resource.cpu_usage_percent) }}</td>
              <td class="text-left">{{ formatPercent(resource.memory_usage_percent) }}</td>
              <td class="text-left">{{ formatPercent(resource.os_disk_usage_percent) }}</td>
              <td class="text-left resource-actions">
                <v-tooltip text="Open logs" location="top">
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="mdi-text-box-search-outline"
                      variant="text"
                      density="comfortable"
                      @click.stop="openComputeResourceLogs(resource)"
                    />
                  </template>
                </v-tooltip>
              </td>
              <td>{{ formatDate(resource.created) }}</td>
              <td>{{ formatDate(resource.started) }}</td>
              <td>{{ formatDate(resource.ended) }}</td>
            </tr>
          </tbody>
        </v-table>
      </div>
    </v-card>

    <ComputeResourceLogsDialog
      v-if="currentItem && selectedResource"
      v-model="logsDialog"
      :calculation-id="currentItem.id"
      :resource="selectedResource"
      :resource-label="resourceLabel"
      :auto-refresh-enabled="!isTerminalCalculation"
    />
  </div>
</template>

<style scoped>
.compute-resource-tables {
  display: grid;
  gap: 16px;
}

.compute-resource-card {
  width: 100%;
}

.resource-table-wrapper {
  overflow-x: auto;
}

.resource-instance-table :deep(.v-table__wrapper > table) {
  min-width: 1360px;
  table-layout: fixed;
}

.resource-instance-table th,
.resource-instance-table td {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.resource-row {
  cursor: pointer;
}

.resource-row:hover {
  background: rgba(var(--v-theme-surface-variant), 0.28);
}

.resource-name-cell {
  align-items: center;
  display: flex;
  gap: 8px;
  min-width: 0;
}

.name-column {
  width: 220px;
}

.status-column {
  width: 112px;
}

.resource-column {
  width: 72px;
}

.price-column {
  width: 112px;
}

.usage-column {
  width: 96px;
}

.actions-column {
  width: 112px;
}

.date-column {
  width: 124px;
}

.usage-cell {
  align-items: center;
  display: inline-flex;
  gap: 4px;
  justify-content: flex-start;
}

.price-cell {
  align-items: center;
  display: inline-flex;
  gap: 6px;
  justify-content: flex-start;
}

.resource-actions {
  white-space: nowrap;
}

.historical-note {
  padding: 0 16px 10px;
}
</style>
